import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Archive, Check, ChevronDown, ChevronLeft as PagePrev, ChevronRight as PageNext, File, FileImage, FileSpreadsheet,
    FileText, Flag, FlagOff, Loader2, Mail, MailOpen, MessagesSquare, Paperclip, Pin, PinOff, RefreshCw, Star, Trash2
} from "@fg/icons";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { cn, getInitials } from "@/lib/utils";
import { useRowSwipe } from "@/lib/rowSwipe";
import { usePreferences, type HoverAction } from "@/lib/preferences";
import { allMessageCategories, mailApi, type AttachmentChip, type MessageCategory, type MessageSummary } from "@/api/mailApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { PdfPreviewDialog } from "./PdfPreviewDialog";
import { QuickViewMessagePanel } from "./QuickViewMessagePanel";
import { canQuickView } from "./quickView";

const MAIL_BFF = import.meta.env.VITE_MAIL_BFF_BASE_URL as string | undefined;

/** Infinite scroll keeps going through this many batches that add no rows to the view (~200 messages). */
const MAX_EMPTY_BATCHES = 4;
import { categoryTheme, isUpdate } from "./categoryTheme";
import { avatarColorClass } from "./avatarColor";
import { emptyFilter, type MessageFilterState } from "./MessageFilters";
import { participantLabel, type Conversation } from "./threading";

/** The inbox's two tabs: mail a person wrote to you, and automated mail. */
export type InboxTab = "direct" | "updates";

const TAB_LABEL: Record<InboxTab, { label: string; title: string }> = {
    direct: { label: "Direct", title: "Mail a person wrote to you" },
    updates: { label: "Updates", title: "Newsletters, notifications, receipts and other automated mail" }
};

type RowAction = "open" | "read" | "unread" | "flag" | "unflag" | "pin" | "unpin" | "archive" | "junk" | "trash";

/** "1–100 of 1,519" (Gmail). */
function pageRange(p: { page: number; pageSize: number; total: number }): string {
    if (p.total === 0) return "No messages";
    const start = (p.page - 1) * p.pageSize + 1;
    const end = Math.min(p.page * p.pageSize, p.total);
    return `${start.toLocaleString()}–${end.toLocaleString()} of ${p.total.toLocaleString()}`;
}

interface MessageListProps {
    messages: MessageSummary[];
    selectedMessageId: string | null;
    onSelectMessage: (id: string) => void;
    folderName: string;
    isLoading?: boolean;
    isError?: boolean;
    errorMessage?: string;

    /** Manual refresh for the current folder. Omit to hide the refresh control entirely. */
    onRefresh?: () => void;
    /** Drives the spinning state on the refresh control while a refetch is in flight. */
    isRefreshing?: boolean;

    /** Multi-select state; the command bar above acts on the checked rows. */
    checkedIds?: Set<string>;
    onToggleChecked?: (id: string) => void;
    onToggleCheckedAll?: (next: boolean) => void;

    /** Row actions — the right-click menu and the hover buttons. Wired by MailPage. */
    onContextAction?: (action: RowAction, id: string) => void;
    /** Hover buttons on rows (off in Drafts, whose rows are drafts, not messages). */
    hoverActionsEnabled?: boolean;

    /** "Create task" in the context menu, with a list picker (To Do lists). */
    taskTargets?: { key: string; label: string }[];
    onCreateTask?: (message: MessageSummary, listKey: string) => void;

    /** Fired on row mouse-enter — MailPage uses this to TanStack-prefetch
     *  the full message body so the reading pane is instant on click. */
    onPrefetchMessage?: (id: string) => void;

    /** Search mode: terms wrapped in <mark> in each row's sender / subject / preview. */
    highlightTerms?: string[];
    /** Conversation view: row id (the conversation's newest message in the page) → its conversation. */
    conversations?: Map<string, Conversation>;
    /** Conversation-view toggle in the header; omit the handler to hide it. */
    conversationView?: boolean;
    onToggleConversationView?: () => void;
    /** Rendered under the header — the search filter chips. */
    banner?: React.ReactNode;
    /** Empty-state text. */
    emptyText?: string;

    /** Combined filter state — driven by the checkbox+dropdown control. */
    filter?: MessageFilterState;
    onFilterChange?: (next: MessageFilterState) => void;
    /** The full, pre-filter message set — used to label filter counts
     *  (e.g. "Unread (12)") independent of what's currently visible. */
    allMessages?: MessageSummary[];

    /** Gmail-style paging for a folder: "1–100 of 9,876" with ‹ ›. */
    pager?: { page: number; pageSize: number; total: number; onPrev: () => void; onNext: () => void };
    /** The folder's real unread count — a page only holds part of it. */
    unreadTotal?: number;
    /** Infinite scroll (folders): the next batch loads as the end of the list nears. Used instead of `pager`. */
    stream?: { total: number; hasMore: boolean; loading: boolean; failed: boolean; onLoadMore: () => void };

    /** Direct / Updates tabs (inbox only); counts are unread messages on the page. */
    tabs?: { value: InboxTab; counts: Record<InboxTab, number>; onChange: (tab: InboxTab) => void };
}

/**
 * Message list, as in the approved mock-up (Doc/MAIL_UI_REDESIGN.md):
 *   - header: folder name, "N unread · 1–100 of N", paging; Direct / Updates
 *     tabs on the inbox; select-all + filter, conversation view, refresh
 *   - sticky date groups (Pinned first), collapsible
 *   - rows: 32 px avatar (the checkbox takes its place on hover / while
 *     selecting), 3 px accent bar + bold when unread, External tag, time;
 *     category pill + subject + attachment / flag / pin icons; preview.
 *     Hover buttons (Archive, Delete, Read, Flag — Settings) replace the time.
 *   - density (Roomy / Cozy / Compact) comes from <html data-density> via the
 *     .mail-row classes in styles/globals.css
 */
export function MessageList({
    messages,
    selectedMessageId,
    onSelectMessage,
    folderName,
    isLoading,
    isError,
    errorMessage,
    onRefresh,
    isRefreshing,
    checkedIds,
    onToggleChecked,
    onToggleCheckedAll,
    onContextAction,
    hoverActionsEnabled = true,
    taskTargets,
    onCreateTask,
    onPrefetchMessage,
    highlightTerms: terms,
    conversations,
    conversationView,
    onToggleConversationView,
    banner,
    emptyText,
    filter,
    onFilterChange,
    allMessages,
    pager,
    unreadTotal,
    tabs,
    stream
}: MessageListProps) {
    const unreadCount = useMemo(() => messages.filter((m) => !m.isRead).length, [messages]);
    const hoverPref = usePreferences((p) => p.list.hoverActions);
    const showPreview = usePreferences((p) => p.general.showPreview);
    const hoverActionsEnabledNow = hoverActionsEnabled && Boolean(onContextAction);
    const hoverActionsStable = useMemo(() => (hoverActionsEnabledNow ? hoverPref.filter(isRowHoverAction) : []), [hoverActionsEnabledNow, hoverPref]);

    // Quick view: click an attachment chip to preview it without opening the
    // message — the reading pane's viewer, with ‹ › across that message's files.
    // Other types download.
    const token = useAccessToken();
    const [quickView, setQuickView] = useState<{ messageId: string; files: AttachmentChip[]; index: number } | null>(null);
    const openAttachment = (m: MessageSummary, chip: AttachmentChip) => {
        if (!chip.id) {
            onSelectMessage(m.id);
            return;
        }
        if (!canQuickView(chip.name)) {
            void mailApi.downloadInboundAttachment(m.id, chip.id, chip.name, token).catch(() => onSelectMessage(m.id));
            return;
        }
        const files = (m.attachmentChips ?? []).filter((c) => c.id && canQuickView(c.name));
        setQuickView({ messageId: m.id, files, index: Math.max(0, files.findIndex((c) => c.id === chip.id)) });
    };
    const viewing = quickView ? quickView.files[quickView.index] : undefined;
    const stepQuickView = (delta: number) =>
        setQuickView((q) => (q && q.files.length > 0 ? { ...q, index: (q.index + delta + q.files.length) % q.files.length } : q));

    // Outlook-style date groups (Today / Yesterday / This Week …). Bucketed
    // into a fixed order rather than split on consecutive rows, so an
    // out-of-order message can never produce a duplicate header.
    // Row times are relative to "now"; it moves once a minute, not on every render or data change.
    const minute = Math.floor(Date.now() / 60_000);
    const now = useMemo(() => new Date(minute * 60_000), [minute]);
    const groups = useMemo(() => {
        const at = new Date();
        const buckets = new Map<DateGroupKey, MessageSummary[]>();
        for (const m of messages) {
            const key = dateGroupOf(m, at);
            const list = buckets.get(key);
            if (list) list.push(m);
            else buckets.set(key, [m]);
        }
        return DATE_GROUP_ORDER.filter((k) => buckets.has(k)).map((key) => ({ key, items: buckets.get(key)! }));
    }, [messages]);
    const [collapsedGroups, setCollapsedGroups] = useState<Set<DateGroupKey>>(() => new Set());
    const toggleGroup = (key: DateGroupKey) =>
        setCollapsedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    // Rows are memoised, so everything handed to them must keep its identity between renders: handlers go
    // through refs to the latest props, and the row reads the message list / selection lazily (drag start only).
    const latest = useRef({ messages, checkedIds, onSelectMessage, onToggleChecked, onContextAction, onPrefetchMessage, openAttachment });
    latest.current = { messages, checkedIds, onSelectMessage, onToggleChecked, onContextAction, onPrefetchMessage, openAttachment };
    const getMessages = useCallback(() => latest.current.messages, []);
    const getCheckedIds = useCallback(() => latest.current.checkedIds, []);
    const selectStable = useCallback((id: string) => latest.current.onSelectMessage(id), []);
    const toggleStable = useCallback((id: string) => latest.current.onToggleChecked?.(id), []);
    const contextActionStable = useCallback((action: RowAction, id: string) => latest.current.onContextAction?.(action, id), []);
    const prefetchStable = useCallback((id: string) => latest.current.onPrefetchMessage?.(id), []);
    const openAttachmentStable = useCallback((m: MessageSummary, chip: AttachmentChip) => latest.current.openAttachment(m, chip), []);
    const openContextMenuStable = useCallback((m: MessageSummary, x: number, y: number) => setContextMenu({ x, y, message: m }), []);

    const selectionActive = Boolean(checkedIds && checkedIds.size > 0);
    const allChecked = Boolean(checkedIds && messages.length > 0 && checkedIds.size === messages.length);
    const someChecked = Boolean(checkedIds && checkedIds.size > 0 && !allChecked);

    // Right-click context menu — single open menu at a time, anchored
    // to viewport coords. Closes on ANY scroll, click, or Escape.
    const [contextMenu, setContextMenu] = useState<
        { x: number; y: number; message: MessageSummary; taskPicker?: boolean } | null
    >(null);
    useEffect(() => {
        if (!contextMenu) return;
        const close = () => setContextMenu(null);
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
        window.addEventListener("scroll", close, true);
        window.addEventListener("click", close);
        window.addEventListener("keydown", onKey);
        return () => {
            window.removeEventListener("scroll", close, true);
            window.removeEventListener("click", close);
            window.removeEventListener("keydown", onKey);
        };
    }, [contextMenu]);

    // j / k move the selection — keep the selected row on screen.
    const listRef = useRef<HTMLElement>(null);
    useEffect(() => {
        if (!selectedMessageId) return;
        listRef.current?.querySelector<HTMLElement>('[aria-current="true"]')?.scrollIntoView({ block: "nearest" });
    }, [selectedMessageId]);

    // Infinite scroll: a sentinel after the last row; once it comes within
    // ~12 rows of the view the next batch loads. If a batch adds no rows here
    // (the other inbox tab, a filter), stop and offer "Load more" rather than
    // reading the whole folder in the background.
    const scrollRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const [nearEnd, setNearEnd] = useState(false);
    const streamOn = Boolean(stream);
    useEffect(() => {
        const root = scrollRef.current;
        const target = sentinelRef.current;
        if (!streamOn || !root || !target) return;
        const io = new IntersectionObserver((entries) => setNearEnd(Boolean(entries[0]?.isIntersecting)), {
            root,
            rootMargin: "0px 0px 900px 0px"
        });
        io.observe(target);
        return () => io.disconnect();
    }, [streamOn, isLoading, isError]);
    // Batches that add nothing to this view (Direct while the mail is mostly
    // notifications) keep loading as long as the end is in sight — up to
    // MAX_EMPTY_BATCHES in a row, then "Load more" takes over.
    const emptyStreak = useRef(0);
    const lastRowCount = useRef(-1);
    const rowCount = messages.length;
    const hasMore = stream?.hasMore ?? false;
    const loadingMore = stream?.loading ?? false;
    const loadFailed = stream?.failed ?? false;
    useEffect(() => {
        if (!stream || !nearEnd || !hasMore || loadingMore || loadFailed) return;
        emptyStreak.current = rowCount === lastRowCount.current ? emptyStreak.current + 1 : 0;
        lastRowCount.current = rowCount;
        if (emptyStreak.current >= MAX_EMPTY_BATCHES) return;
        stream.onLoadMore();
        // `stream` is a fresh object every render — the flags above are the real inputs.
    }, [nearEnd, hasMore, loadingMore, loadFailed, rowCount]);
    const loadMoreNow = () => {
        emptyStreak.current = 0;
        stream?.onLoadMore();
    };

    const unreadPrefix = (unreadTotal ?? 0) > 0 ? `${(unreadTotal ?? 0).toLocaleString()} unread · ` : "";
    const meta = isLoading
        ? "…"
        : stream
            ? `${unreadPrefix}${stream.total.toLocaleString()} message${stream.total === 1 ? "" : "s"}`
        : pager
            // The page holds only part of the folder, so the counts come
            // from the server: "95 unread · 1–100 of 1,519".
            ? `${(unreadTotal ?? 0) > 0 ? `${(unreadTotal ?? 0).toLocaleString()} unread · ` : ""}${pageRange(pager)}`
            : unreadCount > 0
                ? `${unreadCount} unread · ${messages.length} total`
                : `${messages.length} message${messages.length === 1 ? "" : "s"}`;

    return (
        <section ref={listRef} className="flex min-h-0 flex-1 flex-col" aria-label="Message list">
            <header className="flex shrink-0 items-center gap-2 pl-3.5 pr-2 pt-2.5">
                <h2 className="min-w-0 truncate text-base font-semibold leading-[22px]">{folderName}</h2>
                <span className="ml-auto whitespace-nowrap text-xs tabular-nums text-[var(--mail-text-3)]">{meta}</span>
                {pager && pager.total > pager.pageSize ? (
                    <div className="flex items-center" role="group" aria-label="Pages">
                        <ToolButton label="Newer messages" disabled={pager.page <= 1} onClick={pager.onPrev}>
                            <PagePrev className="h-4 w-4" />
                        </ToolButton>
                        <ToolButton label="Older messages" disabled={pager.page * pager.pageSize >= pager.total} onClick={pager.onNext}>
                            <PageNext className="h-4 w-4" />
                        </ToolButton>
                    </div>
                ) : null}
            </header>

            <div className="flex shrink-0 items-end gap-4 border-b border-[var(--mail-line)] pl-3.5 pr-2 pt-1">
                {tabs ? (
                    <div role="tablist" aria-label="Inbox" className="flex gap-4">
                        {(["direct", "updates"] as const).map((t) => {
                            const on = tabs.value === t;
                            const count = tabs.counts[t];
                            return (
                                <button
                                    key={t}
                                    type="button"
                                    role="tab"
                                    aria-selected={on}
                                    title={TAB_LABEL[t].title}
                                    onClick={() => tabs.onChange(t)}
                                    className={cn(
                                        "relative inline-flex h-[34px] items-center gap-1.5 rounded-t text-[13px] text-[var(--mail-text-2)] transition-colors hover:text-foreground",
                                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                                        on && "font-semibold text-foreground after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded-full after:bg-primary"
                                    )}
                                >
                                    {TAB_LABEL[t].label}
                                    {count > 0 ? (
                                        <span
                                            className="rounded-full bg-[var(--mail-primary-soft)] px-1.5 text-[11px] font-semibold leading-4 tabular-nums text-primary"
                                            aria-label={`${count} unread`}
                                        >
                                            {count}
                                        </span>
                                    ) : null}
                                </button>
                            );
                        })}
                    </div>
                ) : null}
                <div className="ml-auto flex h-[38px] items-center gap-0.5">
                    {onToggleCheckedAll ? (
                        <SelectFilterControl
                            allChecked={allChecked}
                            someChecked={someChecked}
                            onToggle={() => onToggleCheckedAll(!allChecked)}
                            filter={filter}
                            onFilterChange={onFilterChange}
                            allMessages={allMessages ?? messages}
                        />
                    ) : null}
                    {onToggleConversationView ? (
                        <ToolButton
                            label={conversationView ? "Conversation view is on — show messages individually" : "Conversation view is off — group replies together"}
                            pressed={Boolean(conversationView)}
                            onClick={onToggleConversationView}
                        >
                            <MessagesSquare className={cn("h-4 w-4", conversationView && "text-primary")} />
                        </ToolButton>
                    ) : null}
                    {onRefresh ? (
                        <ToolButton label="Refresh messages" disabled={isRefreshing} onClick={onRefresh}>
                            <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
                        </ToolButton>
                    ) : null}
                </div>
            </div>

            {banner}

            {/* Keyed by page so a new page starts at the top. A plain scroller (not
                Radix ScrollArea) so the date headers can be position: sticky. */}
            <div ref={scrollRef} key={pager ? `page-${pager.page}` : "list"} className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain">
                <ul role="list">
                    {isLoading ? (
                        <MessageRowSkeletons />
                    ) : isError ? (
                        <li className="px-4 py-8 text-center text-sm text-destructive">
                            {errorMessage ?? "Could not load messages."}
                        </li>
                    ) : messages.length === 0 ? (
                        <li className="px-4 py-10 text-center text-sm text-[var(--mail-text-2)]">{emptyText ?? "No messages in this folder."}</li>
                    ) : (
                        groups.map((g) => {
                            const collapsed = collapsedGroups.has(g.key);
                            return (
                                <Fragment key={g.key}>
                                    <li className="sticky top-0 z-[2] bg-card">
                                        <button
                                            type="button"
                                            onClick={() => toggleGroup(g.key)}
                                            aria-expanded={!collapsed}
                                            className="flex w-full items-center gap-1.5 px-3.5 pb-1 pt-2.5 text-left text-xs font-semibold text-[var(--mail-text-2)] transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                                        >
                                            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", collapsed && "-rotate-90")} aria-hidden />
                                            {g.key === "pinned" ? <Pin className="h-3 w-3" aria-hidden /> : null}
                                            {DATE_GROUP_LABEL[g.key]}
                                            {collapsed ? (
                                                <span className="ml-auto font-normal tabular-nums text-[var(--mail-text-3)]">{g.items.length}</span>
                                            ) : null}
                                        </button>
                                    </li>
                                    {collapsed ? null : g.items.map((m) => (
                                        <MessageRow
                                            key={m.id}
                                            m={m}
                                            now={now}
                                            getMessages={getMessages}
                                            isActive={m.id === selectedMessageId}
                                            isChecked={checkedIds?.has(m.id) ?? false}
                                            getCheckedIds={getCheckedIds}
                                            selectionActive={selectionActive}
                                            conv={conversations?.get(m.id)}
                                            terms={terms}
                                            showPreview={showPreview}
                                            hoverActions={hoverActionsStable}
                                            onSelectMessage={selectStable}
                                            onToggleChecked={onToggleChecked ? toggleStable : undefined}
                                            onContextAction={onContextAction ? contextActionStable : undefined}
                                            onOpenContextMenu={openContextMenuStable}
                                            onPrefetchMessage={onPrefetchMessage ? prefetchStable : undefined}
                                            onOpenAttachment={openAttachmentStable}
                                        />
                                    ))}
                                </Fragment>
                            );
                        })
                    )}
                </ul>
                {stream && !isLoading && !isError ? (
                    <div
                        ref={sentinelRef}
                        role="status"
                        className="flex min-h-12 items-center justify-center gap-2 px-4 py-3 text-xs text-[var(--mail-text-2)]"
                    >
                        {stream.loading ? (
                            <>
                                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Loading more…
                            </>
                        ) : stream.failed ? (
                            <button type="button" onClick={loadMoreNow} className="rounded px-2 py-1 font-semibold text-primary hover:underline">
                                Couldn't load more — try again
                            </button>
                        ) : stream.hasMore ? (
                            <button type="button" onClick={loadMoreNow} className="rounded px-2 py-1 font-semibold text-primary hover:bg-[var(--mail-hover)]">
                                Load more
                            </button>
                        ) : rowCount > 0 ? (
                            <span>That's everything in this folder.</span>
                        ) : null}
                    </div>
                ) : null}
            </div>
            <PdfPreviewDialog
                open={Boolean(viewing)}
                fileName={viewing?.name ?? ""}
                downloadUrl={viewing?.id && quickView && MAIL_BFF
                    ? new URL(
                        `/api/messages/${encodeURIComponent(quickView.messageId)}/attachments/${encodeURIComponent(viewing.id)}`,
                        MAIL_BFF
                    ).toString()
                    : ""}
                accessToken={token}
                onClose={() => setQuickView(null)}
                onPrev={() => stepQuickView(-1)}
                onNext={() => stepQuickView(1)}
                position={quickView && viewing ? { index: quickView.index + 1, total: quickView.files.length } : undefined}
                // Outlook-style: the message beside the file, without opening it in the reader.
                aside={quickView ? (
                    <QuickViewMessagePanel
                        messageId={quickView.messageId}
                        currentAttachmentId={viewing?.id}
                        onPick={(files, index) => setQuickView({ messageId: quickView.messageId, files, index: Math.max(0, index) })}
                        onOpenMessage={() => {
                            onSelectMessage(quickView.messageId);
                            setQuickView(null);
                        }}
                    />
                ) : undefined}
            />
            {contextMenu && onContextAction ? (
                <div
                    role="menu"
                    aria-label={`Context menu for ${contextMenu.message.subject}`}
                    style={{ top: contextMenu.y, left: contextMenu.x }}
                    className="fixed z-50 w-52 overflow-hidden rounded-md border bg-popover py-1 text-popover-foreground shadow-md"
                    onClick={(e) => e.stopPropagation()}
                >
                    <ContextMenuItem onClick={() => { onContextAction("open", contextMenu.message.id); setContextMenu(null); }}>
                        Open
                    </ContextMenuItem>
                    <ContextMenuItem onClick={() => {
                        onContextAction(contextMenu.message.isRead ? "unread" : "read", contextMenu.message.id);
                        setContextMenu(null);
                    }}>
                        {contextMenu.message.isRead ? "Mark as unread" : "Mark as read"}
                    </ContextMenuItem>
                    <ContextMenuItem onClick={() => {
                        onContextAction(contextMenu.message.isFlagged ? "unflag" : "flag", contextMenu.message.id);
                        setContextMenu(null);
                    }}>
                        {contextMenu.message.isFlagged ? "Unflag" : "Flag"}
                    </ContextMenuItem>
                    <ContextMenuItem onClick={() => {
                        onContextAction(contextMenu.message.isPinned ? "unpin" : "pin", contextMenu.message.id);
                        setContextMenu(null);
                    }}>
                        {contextMenu.message.isPinned ? "Unpin" : "Pin to top"}
                    </ContextMenuItem>
                    {onCreateTask && taskTargets && taskTargets.length > 0 ? (
                        <>
                            <ContextMenuItem onClick={() => setContextMenu({ ...contextMenu, taskPicker: !contextMenu.taskPicker })}>
                                <span aria-expanded={Boolean(contextMenu.taskPicker)}>
                                    Create task {contextMenu.taskPicker ? "▾" : "▸"}
                                </span>
                            </ContextMenuItem>
                            {contextMenu.taskPicker ? (
                                <div role="group" aria-label="Create task in list" className="max-h-48 overflow-y-auto">
                                    {taskTargets.map((t) => (
                                        <ContextMenuItem
                                            key={t.key}
                                            className="pl-6 text-muted-foreground hover:text-accent-foreground"
                                            onClick={() => { onCreateTask(contextMenu.message, t.key); setContextMenu(null); }}
                                        >
                                            {t.label}
                                        </ContextMenuItem>
                                    ))}
                                </div>
                            ) : null}
                        </>
                    ) : null}
                    <div className="my-1 h-px bg-border" />
                    <ContextMenuItem onClick={() => { onContextAction("archive", contextMenu.message.id); setContextMenu(null); }}>
                        Archive
                    </ContextMenuItem>
                    <ContextMenuItem onClick={() => { onContextAction("junk", contextMenu.message.id); setContextMenu(null); }}>
                        Move to junk
                    </ContextMenuItem>
                    <ContextMenuItem
                        className="text-destructive"
                        onClick={() => { onContextAction("trash", contextMenu.message.id); setContextMenu(null); }}
                    >
                        Move to trash
                    </ContextMenuItem>
                </div>
            ) : null}
        </section>
    );
}

/** Hover buttons the row can run itself; the rest of the preference list needs a picker. */
type RowHoverAction = Extract<HoverAction, "archive" | "delete" | "read" | "flag" | "pin">;
function isRowHoverAction(a: HoverAction): a is RowHoverAction {
    return a === "archive" || a === "delete" || a === "read" || a === "flag" || a === "pin";
}

/** One row. Memoised: it re-renders only when its own message, state or the (stable) handlers change. */
const MessageRow = memo(MessageRowBase, rowPropsEqual);

/** Props equal for rendering purposes. Conversations are rebuilt whenever the list changes, so compare what a row shows. */
function rowPropsEqual(a: RowProps, b: RowProps): boolean {
    for (const k of Object.keys(a) as Array<keyof RowProps>) {
        if (k === "conv") continue;
        if (a[k] !== b[k]) return false;
    }
    const x = a.conv;
    const y = b.conv;
    if (x === y) return true;
    if (!x || !y) return false;
    return x.messages.length === y.messages.length && x.unread === y.unread && x.flagged === y.flagged
        && x.hasAttachments === y.hasAttachments && x.latest.id === y.latest.id
        && participantLabel(x.participants) === participantLabel(y.participants);
}

interface RowProps {
    m: MessageSummary;
    now: Date;
    getMessages: () => MessageSummary[];
    isActive: boolean;
    isChecked: boolean;
    getCheckedIds: () => Set<string> | undefined;
    selectionActive: boolean;
    conv?: Conversation;
    terms?: string[];
    showPreview: boolean;
    hoverActions: RowHoverAction[];
    onSelectMessage: (id: string) => void;
    onToggleChecked?: (id: string) => void;
    onContextAction?: (action: RowAction, id: string) => void;
    onOpenContextMenu: (m: MessageSummary, x: number, y: number) => void;
    onPrefetchMessage?: (id: string) => void;
    onOpenAttachment?: (m: MessageSummary, chip: AttachmentChip) => void;
}

function MessageRowBase({
    m, now, getMessages, isActive, isChecked, getCheckedIds, selectionActive, conv, terms, showPreview, hoverActions,
    onSelectMessage, onToggleChecked, onContextAction, onOpenContextMenu, onPrefetchMessage, onOpenAttachment
}: RowProps) {
    const count = conv?.messages.length ?? 1;
    const unread = conv ? conv.unread : !m.isRead;
    const flagged = conv?.flagged ?? m.isFlagged;
    const hasAttachments = conv?.hasAttachments ?? m.hasAttachments;
    const senderLabel = conv && count > 1 ? participantLabel(conv.participants) : m.from.name || m.from.email;
    // Automated-mail categories are what the Updates tab is for — no pill for them.
    const theme = m.category && !isUpdate(m.category) ? categoryTheme[m.category] : null;
    const hasHover = hoverActions.length > 0;
    // Touch: swipe right marks read/unread, swipe left archives (see lib/rowSwipe.ts).
    const swipe = useRowSwipe(
        onContextAction
            ? { right: () => onContextAction(unread ? "read" : "unread", m.id), left: () => onContextAction("archive", m.id) }
            : null
    );
    // Attachment chips (mock-up): every chip the server sends (up to three) on
    // one line, shrinking evenly; anything beyond as "+N". Hidden in Compact.
    const chips = (m.attachmentChips ?? []).slice(0, 3);
    const moreChips = Math.max(0, (m.attachmentCount ?? chips.length) - chips.length);

    return (
        <li
            className={cn(
                "mail-row group/row relative grid grid-cols-[40px_minmax(0,1fr)] gap-x-2.5 border-b border-[var(--mail-line)] pl-3.5 pr-3 transition-colors",
                isActive
                    ? "bg-[var(--mail-selected)]"
                    : isChecked
                        ? "bg-[var(--mail-primary-soft)]"
                        : "hover:bg-[var(--mail-hover)]"
            )}
            style={{
                contentVisibility: "auto",
                containIntrinsicSize: "auto var(--mail-row-h)",
                ...(swipe.dx !== 0 || swipe.dragging
                    ? { transform: `translateX(${swipe.dx}px)`, transition: swipe.dragging ? "none" : "transform 150ms ease-out" }
                    : null),
                touchAction: "pan-y"
            }}
            {...swipe.bind}
        >
            {swipe.dx !== 0 ? (
                <span
                    aria-hidden
                    className={cn(
                        "absolute inset-y-0 flex w-[200px] items-center text-white",
                        swipe.dx > 0 ? "right-full justify-end bg-[#0f6cbd] pr-5" : "left-full justify-start bg-[#107c41] pl-5"
                    )}
                >
                    {swipe.dx > 0
                        ? (unread ? <MailOpen className="h-5 w-5" /> : <Mail className="h-5 w-5" />)
                        : <Archive className="h-5 w-5" />}
                </span>
            ) : null}
            {/* Unread: a 3 px accent bar on the left edge. */}
            {unread ? <span aria-hidden className="absolute bottom-2 left-0 top-2 w-[3px] rounded-r-sm bg-primary" /> : null}

            {/* Avatar slot. The selection checkbox takes the avatar's place on
                hover and while anything is selected (Outlook). */}
            <div className="mail-avatar-slot relative mt-0.5 flex justify-center">
                <Avatar
                    className={cn(
                        "mail-avatar",
                        onToggleChecked && (isChecked || selectionActive ? "invisible" : "group-hover/row:invisible")
                    )}
                    style={{ width: "var(--mail-avatar)", height: "var(--mail-avatar)" }}
                >
                    <AvatarFallback className={cn("text-xs font-semibold", avatarColorClass(m.from.email))}>
                        {getInitials(m.from.name || m.from.email)}
                    </AvatarFallback>
                </Avatar>
                {onToggleChecked ? (
                    <div
                        className={cn(
                            "absolute inset-0 items-center justify-center [&>button]:mt-0",
                            isChecked || selectionActive ? "flex" : "hidden group-hover/row:flex group-focus-within/row:flex"
                        )}
                    >
                        <CheckboxCell
                            checked={isChecked}
                            onChange={() => onToggleChecked(m.id)}
                            ariaLabel={isChecked ? `Deselect ${m.subject}` : `Select ${m.subject}`}
                        />
                    </div>
                ) : null}
            </div>

            <button
                type="button"
                onClick={() => onSelectMessage(m.id)}
                // Double-click opens the message full-window in its own tab.
                onDoubleClick={() => {
                    window.open(`/mail/message/${m.id}`, "_blank", "noopener");
                }}
                onMouseEnter={() => onPrefetchMessage?.(m.id)}
                onFocus={() => onPrefetchMessage?.(m.id)}
                onContextMenu={(e) => {
                    if (!onContextAction) return;
                    e.preventDefault();
                    onOpenContextMenu(m, e.clientX, e.clientY);
                }}
                draggable
                onDragStart={(e) => {
                    // Drag to a folder moves; to a task list, creates tasks. A
                    // checked batch travels together.
                    const checkedIds = getCheckedIds();
                    const ids =
                        checkedIds && checkedIds.has(m.id) && checkedIds.size > 1
                            ? Array.from(checkedIds)
                            : [m.id];
                    e.dataTransfer.effectAllowed = "copyMove";
                    e.dataTransfer.setData(
                        "application/x-fg-message",
                        JSON.stringify({
                            id: m.id,
                            fromFolderId: m.folderId,
                            ids,
                            items: ids.map((id) => {
                                const x = getMessages().find((mm) => mm.id === id) ?? m;
                                return { id, subject: x.subject, fromName: x.from.name, fromEmail: x.from.email };
                            })
                        })
                    );
                    e.dataTransfer.setData("text/plain", ids.length > 1 ? `${ids.length} messages` : m.subject);
                }}
                aria-current={isActive ? "true" : undefined}
                aria-label={`${senderLabel}${count > 1 ? `, ${count} messages` : ""}, ${m.subject}${unread ? ", unread" : ""}${hasAttachments ? ", has attachments" : ""}${flagged ? ", flagged" : ""}`}
                className="mail-row-body min-w-0 rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            >
                <div className="flex min-w-0 items-center gap-1.5">
                    <span className={cn("truncate text-sm", unread && "font-semibold")}>
                        <Highlight text={senderLabel} terms={terms} />
                    </span>
                    {count > 1 ? <span className="shrink-0 text-xs tabular-nums text-[var(--mail-text-3)]" aria-hidden>{count}</span> : null}
                    {m.isExternal ? (
                        <span
                            className="inline-flex h-[18px] shrink-0 items-center rounded px-1.5 text-[11px] font-semibold bg-[var(--mail-ext-bg)] text-[var(--mail-ext-fg)]"
                            title={`External sender: ${m.from.email}`}
                        >
                            External
                        </span>
                    ) : null}
                    <time
                        dateTime={m.receivedAt}
                        title={new Date(m.receivedAt).toLocaleString()}
                        className={cn(
                            "ml-auto shrink-0 whitespace-nowrap pl-2 text-xs tabular-nums",
                            unread ? "font-semibold text-primary" : "text-[var(--mail-text-2)]",
                            hasHover && "group-hover/row:invisible group-focus-within/row:invisible"
                        )}
                    >
                        {listDate(m.receivedAt, now)}
                    </time>
                </div>
                <div className="flex min-w-0 items-center gap-1.5">
                    {theme ? (
                        <span className={cn("inline-flex h-5 shrink-0 items-center rounded px-[7px] text-[11px] font-semibold", theme.solid)}>
                            {theme.label}
                        </span>
                    ) : null}
                    <span className={cn("min-w-0 truncate text-[13px]", unread && "font-semibold text-primary")}>
                        <Highlight text={m.subject || "(no subject)"} terms={terms} />
                    </span>
                    <span className="ml-auto flex shrink-0 items-center gap-1 pl-1 text-[var(--mail-text-3)]">
                        {hasAttachments ? <Paperclip className="h-3.5 w-3.5" aria-label="Has attachments" /> : null}
                        {flagged ? <Flag className="h-3.5 w-3.5 fill-[var(--mail-danger)] text-[var(--mail-danger)]" aria-label="Flagged" /> : null}
                        {m.isPinned ? <Pin className="h-3.5 w-3.5 fill-primary text-primary" aria-label="Pinned" /> : null}
                    </span>
                </div>
                {showPreview && m.preview ? (
                    <p className="mail-row-preview truncate text-[13px] text-[var(--mail-text-2)]">
                        <Highlight text={m.preview} terms={terms} />
                    </p>
                ) : null}
            </button>

            {chips.length > 0 ? (
                // One line, as in the mock-up: chips shrink (names truncate) rather
                // than wrap. A click previews the file (or downloads it) without
                // opening the message.
                <div className="mail-row-atts col-start-2 mt-1 flex min-w-0 flex-nowrap items-center gap-1.5 overflow-hidden">
                    {chips.map((a, i) => {
                        const verb = canQuickView(a.name) ? "Quick view" : "Download";
                        return (
                            <button
                                key={`${a.name}-${i}`}
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onOpenAttachment?.(m, a);
                                }}
                                title={`${verb} · ${a.name} · ${formatSize(a.sizeBytes)}`}
                                aria-label={`${verb} ${a.name}`}
                                className="inline-flex h-[26px] min-w-[56px] max-w-[180px] shrink items-center gap-1.5 rounded border border-[var(--mail-line-strong)] bg-card px-2 text-xs text-foreground transition-colors hover:border-primary/50 hover:bg-[var(--mail-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                                <AttachmentGlyph name={a.name} contentType={a.contentType} />
                                <span className="truncate">{a.name}</span>
                            </button>
                        );
                    })}
                    {moreChips > 0 ? (
                        <button
                            type="button"
                            onClick={() => onSelectMessage(m.id)}
                            title="Open the message to see every attachment"
                            className="shrink-0 rounded px-1 text-xs text-[var(--mail-text-2)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                            +{moreChips}
                        </button>
                    ) : null}
                </div>
            ) : null}

            {hasHover && onContextAction ? (
                <div className="absolute right-2.5 top-1.5 z-[1] hidden gap-0.5 rounded bg-card p-0.5 shadow-[var(--mail-shadow-8)] group-focus-within/row:flex group-hover/row:flex">
                    {hoverActions.map((a) => {
                        const spec = hoverSpec(a, { unread, flagged, pinned: Boolean(m.isPinned) });
                        return (
                            <button
                                key={a}
                                type="button"
                                title={spec.label}
                                aria-label={`${spec.label}: ${m.subject}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onContextAction(spec.action, m.id);
                                }}
                                className="grid h-7 w-7 place-items-center rounded text-foreground/80 transition-colors hover:bg-[var(--mail-hover)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                                {spec.icon}
                            </button>
                        );
                    })}
                </div>
            ) : null}
        </li>
    );
}

/** File-type icon in the Office colours: PDF red, sheet green, document blue, image violet. */
function AttachmentGlyph({ name, contentType }: { name: string; contentType: string }) {
    const ext = name.includes(".") ? name.split(".").pop()!.toLowerCase() : "";
    const type = contentType.toLowerCase();
    const cls = "h-3.5 w-3.5 shrink-0";
    if (ext === "pdf" || type === "application/pdf") return <FileText className={cn(cls, "text-[#c42b1c]")} aria-hidden />;
    if (["xls", "xlsx", "xlsm", "csv", "ods"].includes(ext) || type.includes("spreadsheet") || type.includes("excel"))
        return <FileSpreadsheet className={cn(cls, "text-[#107c41]")} aria-hidden />;
    if (["doc", "docx", "odt", "rtf"].includes(ext) || type.includes("word"))
        return <FileText className={cn(cls, "text-[#185abd]")} aria-hidden />;
    if (type.startsWith("image/")) return <FileImage className={cn(cls, "text-[#8764b8]")} aria-hidden />;
    return <File className={cn(cls, "text-[var(--mail-text-2)]")} aria-hidden />;
}

function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function hoverSpec(
    a: RowHoverAction,
    s: { unread: boolean; flagged: boolean; pinned: boolean }
): { action: RowAction; label: string; icon: React.ReactNode } {
    switch (a) {
        case "archive":
            return { action: "archive", label: "Archive", icon: <Archive className="h-4 w-4" /> };
        case "delete":
            return { action: "trash", label: "Delete", icon: <Trash2 className="h-4 w-4" /> };
        case "read":
            return s.unread
                ? { action: "read", label: "Mark as read", icon: <MailOpen className="h-4 w-4" /> }
                : { action: "unread", label: "Mark as unread", icon: <Mail className="h-4 w-4" /> };
        case "flag":
            return s.flagged
                ? { action: "unflag", label: "Unflag", icon: <FlagOff className="h-4 w-4" /> }
                : { action: "flag", label: "Flag", icon: <Flag className="h-4 w-4" /> };
        case "pin":
            return s.pinned
                ? { action: "unpin", label: "Unpin", icon: <PinOff className="h-4 w-4" /> }
                : { action: "pin", label: "Pin to top", icon: <Pin className="h-4 w-4" /> };
    }
}

function ToolButton({
    label, pressed, disabled, onClick, children
}: {
    label: string;
    pressed?: boolean;
    disabled?: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={label}
            aria-label={label}
            aria-pressed={pressed}
            className="grid h-8 w-8 place-items-center rounded text-[var(--mail-text-2)] transition-colors hover:bg-[var(--mail-hover)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-35 disabled:hover:bg-transparent"
        >
            {children}
        </button>
    );
}

type DateGroupKey = "pinned" | "today" | "yesterday" | "thisWeek" | "lastWeek" | "thisMonth" | "lastMonth" | "older";

const DATE_GROUP_ORDER: DateGroupKey[] = ["pinned", "today", "yesterday", "thisWeek", "lastWeek", "thisMonth", "lastMonth", "older"];

const DATE_GROUP_LABEL: Record<DateGroupKey, string> = {
    pinned: "Pinned",
    today: "Today",
    yesterday: "Yesterday",
    thisWeek: "This week",
    lastWeek: "Last week",
    thisMonth: "Earlier this month",
    lastMonth: "Last month",
    older: "Older"
};

function startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Monday-based week, as Outlook shows it for en-GB / en-IN. */
function startOfWeek(d: Date): Date {
    const s = startOfDay(d);
    s.setDate(s.getDate() - ((s.getDay() + 6) % 7));
    return s;
}

function dateGroupOf(m: MessageSummary, now: Date): DateGroupKey {
    if (m.isPinned) return "pinned";
    const d = new Date(m.receivedAt);
    if (Number.isNaN(d.getTime())) return "older";
    const today = startOfDay(now);
    if (d >= today) return "today";
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (d >= yesterday) return "yesterday";
    const week = startOfWeek(now);
    if (d >= week) return "thisWeek";
    const lastWeek = new Date(week);
    lastWeek.setDate(week.getDate() - 7);
    if (d >= lastWeek) return "lastWeek";
    if (d >= new Date(now.getFullYear(), now.getMonth(), 1)) return "thisMonth";
    if (d >= new Date(now.getFullYear(), now.getMonth() - 1, 1)) return "lastMonth";
    return "older";
}

/**
 * Outlook's list-row date: "07:06" today, "Fri 13:22" within the last week,
 * "Sat 06/09" earlier this year, and the year added for anything older.
 */
function listDate(iso: string, now: Date): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    const today = startOfDay(now);
    if (d >= today) return time;
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 6);
    if (d >= weekAgo) return `${d.toLocaleDateString(undefined, { weekday: "short" })} ${time}`;
    return d.toLocaleDateString(undefined, {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" as const } : {})
    });
}

/** Placeholder rows while the folder loads. */
function MessageRowSkeletons() {
    return (
        <>
            {Array.from({ length: 7 }).map((_, i) => (
                <li key={i} className="mail-row grid grid-cols-[40px_minmax(0,1fr)] gap-x-2.5 border-b border-[var(--mail-line)] pl-3.5 pr-3">
                    <div className="mt-0.5 h-8 w-8 animate-pulse rounded-full bg-[var(--mail-subtle)]" />
                    <div className="flex min-w-0 flex-col gap-1.5 pt-1">
                        <div className="h-3 w-1/3 animate-pulse rounded bg-[var(--mail-subtle)]" />
                        <div className="h-3 w-2/3 animate-pulse rounded bg-[var(--mail-subtle)]" />
                        <div className="mail-row-preview h-3 w-1/2 animate-pulse rounded bg-[var(--mail-subtle)]" />
                    </div>
                </li>
            ))}
        </>
    );
}

/**
 * Wraps every case-insensitive occurrence of any search term in &lt;mark&gt;.
 * Returns the text unchanged when there are no terms — keeps the non-search
 * render path zero-cost.
 */
function Highlight({ text, terms }: { text: string; terms?: string[] }) {
    const needles = (terms ?? []).map((t) => t.trim()).filter((t) => t.length > 0);
    if (needles.length === 0) return <>{text}</>;
    // Longest first so "q3 plan" wins over "q3"; escaped — split keeps captures.
    const pattern = needles
        .sort((a, b) => b.length - a.length)
        .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
        .join("|");
    const parts = text.split(new RegExp(`(${pattern})`, "ig"));
    return (
        <>
            {parts.map((part, i) =>
                i % 2 === 1
                    ? (
                        <mark
                            key={i}
                            className="bg-yellow-200 text-foreground dark:bg-yellow-800/60 dark:text-yellow-100"
                        >
                            {part}
                        </mark>
                    )
                    : <span key={i}>{part}</span>
            )}
        </>
    );
}

function ContextMenuItem({
    onClick,
    className,
    children
}: {
    onClick: () => void;
    className?: string;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            role="menuitem"
            onClick={onClick}
            className={cn(
                "block w-full px-3 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:outline-none",
                className
            )}
        >
            {children}
        </button>
    );
}

function CheckboxCell({
    checked, indeterminate, onChange, ariaLabel
}: {
    checked: boolean;
    indeterminate?: boolean;
    onChange: () => void;
    ariaLabel: string;
}) {
    return (
        <button
            type="button"
            role="checkbox"
            aria-checked={indeterminate ? "mixed" : checked}
            aria-label={ariaLabel}
            onClick={onChange}
            className={cn(
                "mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded border focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                checked || indeterminate
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-muted-foreground/40 bg-background hover:border-primary/60"
            )}
        >
            {indeterminate ? (
                <span className="h-0.5 w-2 bg-primary-foreground" aria-hidden />
            ) : checked ? (
                <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden>
                    <path d="M2 6l2.5 2.5L10 3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            ) : null}
        </button>
    );
}

/**
 * Single combined control: the checkbox on the left toggles select-all
 * of the CURRENTLY FILTERED messages; the attached caret opens the
 * filter menu — "All" by default, or narrow by read/unread, flagged,
 * attachments, or one of the category labels (multi-select).
 */
function SelectFilterControl({
    allChecked,
    someChecked,
    onToggle,
    filter,
    onFilterChange,
    allMessages
}: {
    allChecked: boolean;
    someChecked: boolean;
    onToggle: () => void;
    filter?: MessageFilterState;
    onFilterChange?: (next: MessageFilterState) => void;
    allMessages: MessageSummary[];
}) {
    const activeFilter = filter ?? emptyFilter();
    const activeCount =
        activeFilter.categories.size +
        (activeFilter.unread !== null ? 1 : 0) +
        (activeFilter.flagged ? 1 : 0) +
        (activeFilter.hasAttachments ? 1 : 0);
    const filterLabel = activeCount === 0 ? "All" : describeFilter(activeFilter);

    const categoryCounts = useMemo(() => {
        const m = new Map<MessageCategory, number>();
        for (const msg of allMessages) {
            if (msg.category) m.set(msg.category, (m.get(msg.category) ?? 0) + 1);
        }
        return m;
    }, [allMessages]);

    const unreadN = useMemo(() => allMessages.filter((m) => !m.isRead).length, [allMessages]);
    const flaggedN = useMemo(() => allMessages.filter((m) => m.isFlagged).length, [allMessages]);
    const attachN = useMemo(() => allMessages.filter((m) => m.hasAttachments).length, [allMessages]);

    const set = (patch: Partial<MessageFilterState>) => {
        if (!onFilterChange) return;
        onFilterChange({ ...activeFilter, ...patch });
    };
    const toggleCategory = (c: MessageCategory) => {
        if (!onFilterChange) return;
        const next = new Set(activeFilter.categories);
        if (next.has(c)) next.delete(c); else next.add(c);
        onFilterChange({ ...activeFilter, categories: next });
    };

    return (
        <div className="flex h-7 items-center rounded border border-[var(--mail-line-strong)] bg-card">
            <button
                type="button"
                role="checkbox"
                aria-checked={someChecked ? "mixed" : allChecked}
                aria-label={allChecked ? "Deselect all filtered" : "Select all filtered"}
                title={allChecked ? "Deselect all filtered" : "Select all filtered"}
                onClick={onToggle}
                className="flex h-full items-center justify-center rounded-l px-1.5 hover:bg-[var(--mail-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
                <span
                    aria-hidden
                    className={cn(
                        "flex h-4 w-4 items-center justify-center rounded border",
                        (allChecked || someChecked)
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-muted-foreground/40 bg-background"
                    )}
                >
                    {someChecked ? (
                        <span className="h-0.5 w-2 bg-current" />
                    ) : allChecked ? (
                        <Check className="h-3 w-3" />
                    ) : null}
                </span>
            </button>
            {onFilterChange ? (
                <DropdownMenu>
                    <DropdownMenuTrigger
                        aria-label={`Filter: ${filterLabel}`}
                        title={`Filter: ${filterLabel}`}
                        className="flex h-full items-center gap-1 rounded-r border-l border-[var(--mail-line-strong)] px-2 text-xs hover:bg-[var(--mail-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                        <span className="max-w-[12ch] truncate">{filterLabel}</span>
                        {activeCount > 0 ? (
                            <span className="rounded-full bg-primary/20 px-1 text-[10px] font-semibold text-primary">
                                {activeCount}
                            </span>
                        ) : null}
                        <ChevronDown className="h-3 w-3" aria-hidden />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-60">
                        <DropdownMenuItem
                            onClick={(e) => { e.preventDefault(); onFilterChange(emptyFilter()); }}
                        >
                            <Check className={cn("h-3.5 w-3.5", activeCount === 0 ? "opacity-100" : "opacity-0")} />
                            <span className="flex-1">All</span>
                            <span className="text-[10px] text-muted-foreground">{allMessages.length}</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuCheckboxItem
                            className="gap-2"
                            checked={activeFilter.unread === true}
                            onCheckedChange={(v) => set({ unread: v ? true : null })}
                        >
                            <Mail className="h-3.5 w-3.5" />
                            <span className="flex-1">Unread</span>
                            <span className="text-[10px] text-muted-foreground">{unreadN}</span>
                        </DropdownMenuCheckboxItem>
                        <DropdownMenuCheckboxItem
                            className="gap-2"
                            checked={activeFilter.unread === false}
                            onCheckedChange={(v) => set({ unread: v ? false : null })}
                        >
                            <MailOpen className="h-3.5 w-3.5" />
                            <span className="flex-1">Read</span>
                            <span className="text-[10px] text-muted-foreground">{allMessages.length - unreadN}</span>
                        </DropdownMenuCheckboxItem>
                        <DropdownMenuCheckboxItem
                            className="gap-2"
                            checked={activeFilter.flagged}
                            onCheckedChange={(v) => set({ flagged: Boolean(v) })}
                        >
                            <Star className="h-3.5 w-3.5" />
                            <span className="flex-1">Flagged</span>
                            <span className="text-[10px] text-muted-foreground">{flaggedN}</span>
                        </DropdownMenuCheckboxItem>
                        <DropdownMenuCheckboxItem
                            className="gap-2"
                            checked={activeFilter.hasAttachments}
                            onCheckedChange={(v) => set({ hasAttachments: Boolean(v) })}
                        >
                            <Paperclip className="h-3.5 w-3.5" />
                            <span className="flex-1">Attachments</span>
                            <span className="text-[10px] text-muted-foreground">{attachN}</span>
                        </DropdownMenuCheckboxItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                            Categories
                        </DropdownMenuLabel>
                        {allMessageCategories.map((c) => {
                            const theme = categoryTheme[c];
                            const count = categoryCounts.get(c) ?? 0;
                            return (
                                <DropdownMenuCheckboxItem
                                    key={c}
                                    className="gap-2"
                                    checked={activeFilter.categories.has(c)}
                                    onCheckedChange={() => toggleCategory(c)}
                                >
                                    <span className={cn("h-2 w-2 shrink-0 rounded-full", theme.dot)} aria-hidden />
                                    <span className="flex-1">{theme.label}</span>
                                    <span className="text-[10px] text-muted-foreground">{count}</span>
                                </DropdownMenuCheckboxItem>
                            );
                        })}
                    </DropdownMenuContent>
                </DropdownMenu>
            ) : null}
        </div>
    );
}

function describeFilter(f: MessageFilterState): string {
    const parts: string[] = [];
    if (f.unread === true) parts.push("Unread");
    if (f.unread === false) parts.push("Read");
    if (f.flagged) parts.push("Flagged");
    if (f.hasAttachments) parts.push("Attachments");
    if (f.categories.size === 1) parts.push(categoryTheme[[...f.categories][0]!].label);
    else if (f.categories.size > 1) parts.push(`${f.categories.size} categories`);
    if (parts.length === 0) return "All";
    if (parts.length === 1) return parts[0]!;
    return `${parts[0]} +${parts.length - 1}`;
}
